import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { AiReplyRating } from './aiChatService'
import { toPlainText, truncateForShare } from './replyText'

// Action bar under each assistant reply: copy, rate, share, read aloud and
// ask again. Reading aloud is coordinated by the widget (one reply at a time).

const Icon = ({ children }: { children: ReactNode }) => (
    <svg
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        viewBox="0 0 24 24"
    >
        {children}
    </svg>
)

const CopyIcon = () => (
    <Icon>
        <rect height="12" rx="2" width="12" x="8" y="8" />
        <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
    </Icon>
)

const CheckIcon = () => (
    <Icon>
        <path d="m5 12 5 5L19 7" />
    </Icon>
)

const ThumbUpIcon = () => (
    <Icon>
        <path d="M7 11v9H4v-9h3Zm0 0 4-8a2 2 0 0 1 2 2v4h5a2 2 0 0 1 2 2.3l-1.2 7A2 2 0 0 1 16.8 20H7" />
    </Icon>
)

const ThumbDownIcon = () => (
    <Icon>
        <path d="M17 13V4h3v9h-3Zm0 0-4 8a2 2 0 0 1-2-2v-4H6a2 2 0 0 1-2-2.3l1.2-7A2 2 0 0 1 7.2 4H17" />
    </Icon>
)

const ShareIcon = () => (
    <Icon>
        <path d="M12 15V3m0 0-4 4m4-4 4 4M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
    </Icon>
)

const SpeakerIcon = () => (
    <Icon>
        <path d="M4 9v6h4l5 4V5L8 9H4Z" />
        <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" />
    </Icon>
)

const StopIcon = () => (
    <Icon>
        <rect height="10" rx="1.5" width="10" x="7" y="7" />
    </Icon>
)

const RetryIcon = () => (
    <Icon>
        <path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7" />
    </Icon>
)

const SHARE_SUBJECT = 'From the OASYS HR Assistant'

type ShareTarget = 'whatsapp' | 'email' | 'x' | 'instagram'

type ReplyActionsProps = {
    /** The reply as markdown. */
    text: string
    rating?: AiReplyRating
    speaking: boolean
    canSpeak: boolean
    retryDisabled: boolean
    onRate: (rating: AiReplyRating) => void
    onSpeak: (plainText: string) => void
    /** Absent when there is no question to ask again. */
    onRetry?: () => void
}

export const ReplyActions = ({
    text,
    rating,
    speaking,
    canSpeak,
    retryDisabled,
    onRate,
    onSpeak,
    onRetry,
}: ReplyActionsProps) => {
    const [copied, setCopied] = useState(false)
    const [shareOpen, setShareOpen] = useState(false)
    const [notice, setNotice] = useState('')
    const timerRef = useRef<number>()

    useEffect(() => () => window.clearTimeout(timerRef.current), [])

    const flash = (message: string) => {
        setNotice(message)
        window.clearTimeout(timerRef.current)
        timerRef.current = window.setTimeout(() => setNotice(''), 2500)
    }

    const copyText = async (plain: string): Promise<boolean> => {
        try {
            await navigator.clipboard.writeText(plain)
            return true
        } catch {
            return false
        }
    }

    const copy = async () => {
        if (await copyText(toPlainText(text))) {
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1500)
        } else {
            flash('Couldn’t copy - your browser blocked clipboard access.')
        }
    }

    const share = async (target: ShareTarget) => {
        const plain = toPlainText(text)
        const open = (url: string) =>
            window.open(url, '_blank', 'noopener,noreferrer')
        switch (target) {
            case 'whatsapp':
                open(
                    `https://wa.me/?text=${encodeURIComponent(truncateForShare(plain, 3000))}`,
                )
                break
            case 'email':
                // mailto: bodies are limited by the mail app; keep it short.
                window.location.href = `mailto:?subject=${encodeURIComponent(SHARE_SUBJECT)}&body=${encodeURIComponent(truncateForShare(plain, 1800))}`
                break
            case 'x':
                open(
                    `https://x.com/intent/post?text=${encodeURIComponent(truncateForShare(plain, 270))}`,
                )
                break
            case 'instagram':
                // Instagram has no web link that pre-fills text: copy it and
                // open Instagram for the user to paste.
                if (await copyText(plain)) {
                    open('https://www.instagram.com/direct/inbox/')
                    flash('Copied - paste it in Instagram.')
                } else {
                    flash(
                        'Couldn’t copy - your browser blocked clipboard access.',
                    )
                }
                return
        }
        setShareOpen(false)
    }

    return (
        <div className="aic-actions-wrap">
            <div
                aria-label="Reply actions"
                className="aic-actions"
                role="toolbar"
            >
                <button
                    aria-label={copied ? 'Copied' : 'Copy reply'}
                    className="aic-action"
                    title={copied ? 'Copied' : 'Copy'}
                    type="button"
                    onClick={copy}
                >
                    {copied ? <CheckIcon /> : <CopyIcon />}
                </button>
                <button
                    aria-label="Good response"
                    aria-pressed={rating === 'GOOD'}
                    className={`aic-action${rating === 'GOOD' ? ' aic-action--active' : ''}`}
                    title="Good response"
                    type="button"
                    onClick={() => onRate('GOOD')}
                >
                    <ThumbUpIcon />
                </button>
                <button
                    aria-label="Bad response"
                    aria-pressed={rating === 'BAD'}
                    className={`aic-action${rating === 'BAD' ? ' aic-action--active' : ''}`}
                    title="Bad response"
                    type="button"
                    onClick={() => onRate('BAD')}
                >
                    <ThumbDownIcon />
                </button>
                <button
                    aria-expanded={shareOpen}
                    aria-label="Share reply"
                    className={`aic-action${shareOpen ? ' aic-action--active' : ''}`}
                    title="Share"
                    type="button"
                    onClick={() => setShareOpen((v) => !v)}
                >
                    <ShareIcon />
                </button>
                {canSpeak && (
                    <button
                        aria-label={speaking ? 'Stop reading' : 'Read aloud'}
                        aria-pressed={speaking}
                        className={`aic-action${speaking ? ' aic-action--active' : ''}`}
                        title={speaking ? 'Stop reading' : 'Read aloud'}
                        type="button"
                        onClick={() => onSpeak(toPlainText(text))}
                    >
                        {speaking ? <StopIcon /> : <SpeakerIcon />}
                    </button>
                )}
                {onRetry && (
                    <button
                        aria-label="Try again"
                        className="aic-action"
                        disabled={retryDisabled}
                        title="Try again"
                        type="button"
                        onClick={onRetry}
                    >
                        <RetryIcon />
                    </button>
                )}
                {notice && (
                    <span className="aic-action-notice" role="status">
                        {notice}
                    </span>
                )}
            </div>

            {shareOpen && (
                <div
                    aria-label="Share this reply"
                    className="aic-share"
                    role="dialog"
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            e.stopPropagation()
                            setShareOpen(false)
                        }
                    }}
                >
                    <p className="aic-share-warning">
                        Shared text leaves the HR portal. Don’t share employees’
                        personal details.
                    </p>
                    <div className="aic-share-options">
                        <button
                            className="aic-share-option"
                            type="button"
                            onClick={() => share('whatsapp')}
                        >
                            WhatsApp
                        </button>
                        <button
                            className="aic-share-option"
                            type="button"
                            onClick={() => share('email')}
                        >
                            Email
                        </button>
                        <button
                            className="aic-share-option"
                            type="button"
                            onClick={() => share('x')}
                        >
                            X
                        </button>
                        <button
                            className="aic-share-option"
                            type="button"
                            onClick={() => share('instagram')}
                        >
                            Instagram
                        </button>
                    </div>
                    <button
                        className="aic-share-cancel"
                        type="button"
                        onClick={() => setShareOpen(false)}
                    >
                        Cancel
                    </button>
                </div>
            )}
        </div>
    )
}
